"use client";

import { motion, useReducedMotion, type Variants } from "motion/react";
import { type FocusEvent, type PointerEvent, useEffect, useState } from "react";

import { EASE_OUT_EXPO } from "@/components/motion/reveal";
import { Image } from "@/components/shared/image";
import { Link } from "@/i18n/navigation";
import type { MediaAsset } from "@/lib/media";
import { cn } from "@/lib/utils";

import { useReveal } from "../../_lib/use-reveal";

// =============================================================================

/** Delay between two screenshots of the hover slideshow, in ms */
const SLIDE_INTERVAL = 1500;

const cardVariants = (column: number): Variants => ({
  hidden: {},
  visible: { transition: { delayChildren: column * 0.12 } },
});

// The frame rises like a curtain while the screenshot zooms out inside it
const frameVariants: Variants = {
  hidden: { clipPath: "inset(100% 0% 0% 0% round 1.25rem)", y: 48 },
  visible: {
    clipPath: "inset(0% 0% 0% 0% round 1.25rem)",
    y: 0,
    transition: { duration: 1.2, ease: EASE_OUT_EXPO },
  },
};

const imageVariants: Variants = {
  hidden: { scale: 1.25 },
  visible: { scale: 1, transition: { duration: 1.6, ease: EASE_OUT_EXPO } },
};

const textVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: 0.25, ease: EASE_OUT_EXPO },
  },
};

// =============================================================================

export type GridProject = {
  id: string;
  slug: string;
  name: string;
  tagline: string;
  slides: MediaAsset[];
};

type ProjectCardProps = {
  project: GridProject;
  index: number;
  isActive: boolean;
  isDimmed: boolean;
  onActivate: () => void;
  onDeactivate: () => void;
};

/**
 * Screenshot in a device-like frame, then the name and the tagline. Hovered
 * (or focused with the keyboard), the frame lights up, the screenshot zooms
 * in and cycles through the gallery.
 */
export function ProjectCard({
  project,
  index,
  isActive,
  isDimmed,
  onActivate,
  onDeactivate,
}: ProjectCardProps) {
  const shouldReduceMotion = useReducedMotion();
  const { ref, isRevealed } = useReveal<HTMLLIElement>();
  const [slide, setSlide] = useState(0);
  // The gallery screenshots are only loaded once the card has been hovered
  const [hasBeenActive, setHasBeenActive] = useState(false);
  const { slides } = project;
  const currentSlide = isActive ? slide : 0;

  useEffect(() => {
    if (!isActive || slides.length < 2 || shouldReduceMotion) return;
    const timer = window.setInterval(() => {
      setSlide((current) => (current + 1) % slides.length);
    }, SLIDE_INTERVAL);
    return () => window.clearInterval(timer);
  }, [isActive, slides.length, shouldReduceMotion]);

  const activate = () => {
    setSlide(0);
    setHasBeenActive(true);
    onActivate();
  };

  // Touch devices simply follow the link: no hover state to get stuck
  const onPointerEnter = (event: PointerEvent) => {
    if (event.pointerType === "mouse") activate();
  };
  const onPointerLeave = (event: PointerEvent) => {
    if (event.pointerType === "mouse") onDeactivate();
  };
  const onFocus = (event: FocusEvent<HTMLAnchorElement>) => {
    if (event.currentTarget.matches(":focus-visible")) activate();
  };

  return (
    <motion.li
      ref={ref}
      initial="hidden"
      animate={isRevealed ? "visible" : "hidden"}
      variants={cardVariants(index % 2)}
    >
      <Link
        href={`/projects/${project.slug}`}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        onFocus={onFocus}
        onBlur={onDeactivate}
        className="group block rounded-[1.4rem]"
      >
        <motion.div
          variants={frameVariants}
          className="relative rounded-[1.25rem] bg-foreground/25 p-1.5 sm:p-2 dark:bg-foreground/50"
        >
          {/* Highlighted frame */}
          <span
            aria-hidden
            className={cn(
              "absolute inset-0 rounded-[inherit] bg-foreground opacity-0 transition-opacity duration-500 ease-out-quart",
              isActive && "opacity-100"
            )}
          />
          <div className="relative aspect-video [transform:translateZ(0)] overflow-hidden rounded-[0.8rem] bg-surface-2">
            <motion.div variants={imageVariants} className="absolute inset-0">
              <div
                className={cn(
                  "absolute inset-0 origin-[50%_35%] transition-transform duration-[1100ms] ease-out-expo motion-reduce:transition-none",
                  isActive && "scale-[1.15] motion-reduce:scale-100"
                )}
              >
                {slides.map((image, slideIndex) =>
                  slideIndex === 0 || hasBeenActive ? (
                    <Image
                      key={image.url}
                      src={image.url}
                      alt={slideIndex === 0 ? image.alt || project.name : ""}
                      fill
                      sizes="(min-width: 768px) 46vw, 100vw"
                      loading={index < 2 && slideIndex === 0 ? "eager" : "lazy"}
                      fetchPriority={
                        index < 2 && slideIndex === 0 ? "high" : "auto"
                      }
                      className={cn(
                        "object-cover object-top transition-opacity duration-700 ease-out-quart",
                        slideIndex === currentSlide
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                  ) : null
                )}
              </div>
            </motion.div>
            {/* Dims the other cards while one is hovered */}
            <span
              aria-hidden
              className={cn(
                "pointer-events-none absolute inset-0 bg-background/65 opacity-0 transition-opacity duration-500 ease-out-quart",
                isDimmed && "opacity-100"
              )}
            />
          </div>
        </motion.div>

        <motion.div variants={textVariants} className="mt-4 sm:mt-5">
          <div
            className={cn(
              "transition-opacity duration-500 ease-out-quart",
              isDimmed && "opacity-45"
            )}
          >
            <h2 className="text-[clamp(1.5rem,2.1vw,1.875rem)] leading-tight font-bold tracking-[-0.01em]">
              {project.name}
            </h2>
            {project.tagline && (
              <p className="mt-1 text-[0.95rem] text-muted-foreground sm:text-base">
                {project.tagline}
              </p>
            )}
          </div>
        </motion.div>
      </Link>
    </motion.li>
  );
}

// =============================================================================
